import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Trash, CircleNotch, ShieldCheck } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SOURCE_LABELS = { plaid: "Bank sync", manual: "Manual entry", receipt: "Receipt", csv: "Imported", email: "Emailed receipt" };

export function DuplicateCompareDialog({ anomaly, onClose, onResolved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (!anomaly) { setData(null); return; }
    setLoading(true);
    api.get(`/anomalies/${anomaly.id}/comparison`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Couldn't load the charges for comparison."); onClose?.(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anomaly?.id]);

  const removeTxn = async (txnId) => {
    setBusyId(txnId);
    try {
      await api.post(`/anomalies/${anomaly.id}/resolve-duplicate`, { delete_txn_id: txnId });
      toast.success("Duplicate removed — your totals are updated and this alert is cleared.");
      onResolved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove that charge.");
    } finally {
      setBusyId("");
    }
  };

  const rows = data ? [
    ...(data.flagged ? [{ ...data.flagged, __tag: "Flagged charge" }] : []),
    ...(data.related || []).map((t) => ({ ...t, __tag: "Earlier charge" })),
  ] : [];

  return (
    <Dialog open={!!anomaly} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-xl rounded-[28px] border-0 shadow-[0_24px_60px_rgba(26,39,68,0.18)]" data-testid="duplicate-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
            <Copy size={20} weight="duotone" color="#B23B1F" /> Side by side: the charges Rowan flagged
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
          Rowan never deletes money records on her own — you decide. If one of these shouldn't be there, remove it and your books update instantly. If both are real, close this and hit "Looks fine."
        </p>
        {loading ? (
          <p className="text-sm inline-flex items-center gap-2 py-4" style={{ color: "var(--bk-text-2)" }} data-testid="duplicate-compare-loading">
            <CircleNotch size={16} className="animate-spin" /> Pulling up both charges…
          </p>
        ) : rows.length <= 1 ? (
          <p className="text-sm py-2" style={{ color: "var(--bk-text-2)" }} data-testid="duplicate-compare-empty">
            The other charge isn't in the books anymore — it may already be deleted. You can safely mark this alert "Looks fine."
          </p>
        ) : (
          <div className="space-y-2" data-testid="duplicate-compare-rows">
            {rows.map((t) => (
              <div key={t.id} className="rounded-xl border p-3 flex items-center justify-between gap-3" style={{ borderColor: t.__tag === "Flagged charge" ? "rgba(217,108,74,0.4)" : "var(--bk-border)", background: t.__tag === "Flagged charge" ? "#FFF8F5" : "#fff" }} data-testid={`duplicate-compare-row-${t.id}`}>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: t.__tag === "Flagged charge" ? "#FDECE8" : "var(--bk-page)", color: t.__tag === "Flagged charge" ? "#B23B1F" : "var(--bk-text-2)" }}>{t.__tag}</span>
                  <p className="text-sm font-semibold mt-1 truncate" style={{ color: "var(--bk-text)" }}>{t.description || "Transaction"}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>
                    {t.date} · {t.category || "Uncategorized"} · {SOURCE_LABELS[t.source] || t.source || "manual"}{t.receipt_id ? " · has receipt" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-bold" style={{ color: "var(--bk-text)" }}>{money(t.amount)}</span>
                  <button
                    onClick={() => removeTxn(t.id)}
                    disabled={!!busyId}
                    data-testid={`duplicate-delete-${t.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
                    style={{ background: "#B23B1F" }}
                  >
                    {busyId === t.id ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} weight="bold" />} Delete this one
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs inline-flex items-center gap-1.5" style={{ color: "var(--bk-text-2)" }}>
          <ShieldCheck size={13} weight="fill" /> Deleting one clears this alert and logs the change in your activity history.
        </p>
      </DialogContent>
    </Dialog>
  );
}
